import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Avatar
} from '@mui/material';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { doc, setDoc } from 'firebase/firestore';
import { Customer } from '../../firebase/services';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useLanguage } from '../../context/LanguageContext';

interface UserCreationFormProps {
  customers: Customer[];
  onUserCreated: (userId: string, userData: any) => void;
  onError: (message: string) => void;
}

const UserCreationForm: React.FC<UserCreationFormProps> = ({ 
  customers, 
  onUserCreated, 
  onError 
}) => {
  const { t } = useLanguage();
  
  // États pour le formulaire
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [roleId, setRoleId] = useState<number>(2); // Par défaut, rôle client
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Validation des formulaires
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    displayName?: string;
    avatarUrl?: string;
  }>({});

  // Validation du formulaire utilisateur
  const validateForm = () => {
    const newErrors: {
      email?: string;
      password?: string;
      confirmPassword?: string;
      displayName?: string;
      avatarUrl?: string;
    } = {};
    
    if (!email) {
      newErrors.email = t('admin.userForm.error.email.required') as string;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('admin.userForm.error.email.invalid') as string;
    }
    
    if (!password) {
      newErrors.password = t('admin.userForm.error.password.required') as string;
    } else if (password.length < 6) {
      newErrors.password = t('admin.userForm.error.password.length') as string;
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = t('admin.userForm.error.password.mismatch') as string;
    }
    
    if (!displayName) {
      newErrors.displayName = t('admin.userForm.error.displayName.required') as string;
    }
    
    if (avatarUrl && !isValidUrl(avatarUrl)) {
      newErrors.avatarUrl = t('admin.userForm.error.avatarUrl.invalid') as string;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Vérifier si une URL est valide
  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Créer un nouvel utilisateur
  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setCreating(true);
      
      // Créer l'utilisateur dans Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Préparer les données utilisateur
      const userData = {
        email,
        displayName,
        jobTitle,
        avatarUrl,
        roleId,
        customerId: selectedCustomerId || null,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Créer le profil utilisateur dans Firestore
      await setDoc(doc(db, 'users', user.uid), userData);
      
      // Réinitialiser le formulaire
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setDisplayName('');
      setJobTitle('');
      setAvatarUrl('');
      setRoleId(2);
      setSelectedCustomerId('');
      
      // Notifier le composant parent
      onUserCreated(user.uid, userData);
      
    } catch (error: any) {
      console.error('Error creating user:', error);
      let errorMessage = t('admin.userForm.error.generic') as string;
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = t('admin.userForm.error.emailExists') as string;
      }
      
      onError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3, 
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 3,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <PersonAddIcon />
        {t('admin.userForm.title')}
      </Typography>
      
      <form onSubmit={handleCreateUser}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.userForm.email')}
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!errors.email}
              helperText={errors.email}
              required
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.userForm.displayName')}
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              error={!!errors.displayName}
              helperText={errors.displayName}
              required
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.userForm.jobTitle')}
              fullWidth
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="CEO, CTO, Director, Team Leader..."
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.userForm.avatarUrl')}
              fullWidth
              value={avatarUrl}
              onChange={(e) => setAvatarUrl(e.target.value)}
              placeholder="https://example.com/avatar.jpg"
              error={!!errors.avatarUrl}
              helperText={errors.avatarUrl}
            />
          </Grid>
          
          {avatarUrl && (
            <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <Avatar 
                src={avatarUrl} 
                alt={displayName}
                sx={{ width: 80, height: 80 }}
              />
            </Grid>
          )}
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.userForm.password')}
              type={showPassword ? "text" : "password"}
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={!!errors.password}
              helperText={errors.password}
              required
              InputProps={{
                endAdornment: (
                  <Button 
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </Button>
                )
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('admin.userForm.confirmPassword')}
              type={showPassword ? "text" : "password"}
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              required
            />
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('admin.userForm.role')}</InputLabel>
              <Select
                value={roleId}
                onChange={(e) => setRoleId(Number(e.target.value))}
                label={t('admin.userForm.role')}
              >
                <MenuItem value={1}>{t('admin.userForm.role.admin')}</MenuItem>
                <MenuItem value={2}>{t('admin.userForm.role.client')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('admin.userForm.associatedCustomer')}</InputLabel>
              <Select
                value={selectedCustomerId}
                onChange={(e) => setSelectedCustomerId(e.target.value)}
                label={t('admin.userForm.associatedCustomer')}
              >
                <MenuItem value="">{t('admin.userForm.none')}</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.companyName} ({customer.name})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={creating}
              startIcon={creating ? <CircularProgress size={20} /> : <PersonAddIcon />}
            >
              {creating ? t('admin.userForm.creating') : t('admin.userForm.create')}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default UserCreationForm; 